<template>
  <section v-if="metals.length" class="grid gap-16">
    <h3 class="raw-cta-2">{{ i18n(translations.relatedMetals) }}</h3>
    <DraggableRail>
      <li
        v-for="metal in metals"
        :key="metal.id"
        class="rounded-md relative grid aspect-square shrink-0 place-items-center gap-8 bg-[white]/25 p-16"
      >
        <!-- Whole chip is the hit area: one transparent button spans it and
                     carries the click + accessible name; the box below is decorative. -->
        <button
          type="button"
          class="rounded-md absolute inset-0 z-[1]"
          :aria-label="i18n(metal.name)"
          @click="openMetal(metal)"
        />
        <MetalImage :metal :size="56" />
        <p class="raw-cta-3 text-center !leading-[1.25]">
          {{ i18n(metal.name) }}
        </p>
      </li>
    </DraggableRail>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'

import DraggableRail from '~/scopes/campaign/components/UI/DraggableRail.vue'
import MetalImage from '~/scopes/campaign/components/UI/MetalImage.vue'
import { useModalState } from '~/scopes/campaign/composables/useModalState'
import { MODAL_ROUTES } from '~/scopes/campaign/const/config.const'
import { relatedMetals } from '~/scopes/campaign/data/metals/data'
import type { Metal } from '~/scopes/campaign/data/metals/types'

import translations from './RelatedMetals.translations'

const props = defineProps<{
  metalSlug: string
  /** Tracking surface of the host view — the rail lives in the panel and the reader. */
  zone: string
}>()

const i18n = useI18n()
const { trackClick } = useTracking()
const { navigateToDetail } = useModalState()

const metals = computed(() => relatedMetals(props.metalSlug))

function openMetal(metal: Metal) {
  trackClick({
    zone: props.zone,
    name: `related-metal-${metal.slug}`,
    campaign: 'raw-materials',
  })
  // The metal's own URL, like the stories rail beside it — a change of subject, not a peek.
  navigateToDetail(MODAL_ROUTES.METAL_LIBRARY, metal.slug)
}
</script>

<style scoped>
@media (hover: hover) {
  li {
    transition: background-color var(--raw-out-quart);

    :deep(img) {
      transition: transform var(--raw-out-quart);
    }

    &:hover {
      background-color: color-mix(in oklab, white, transparent);

      :deep(img) {
        transform: scale(108%) rotate(-3deg);
      }
    }
  }
}
</style>
